import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useUserRole } from "@/hooks/useUserRole";
import { TemplateCard } from "@/components/composer/templates/TemplateCard";
import { TemplateEditor } from "@/components/composer/templates/TemplateEditor";
import { TemplatePreviewDialog } from "@/components/composer/templates/TemplatePreviewDialog";
import {
  CONTENT_TYPE_OPTIONS,
  createExcerptusTemplate,
  deleteExcerptusTemplate,
  fetchAllExcerptusTemplates,
  templateKind,
  type ExcerptusTemplate,
} from "@/lib/composerTemplates";
import { AppSeo } from "@/components/AppSeo";

const AdminComposerTemplates = () => {
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [templates, setTemplates] = useState<ExcerptusTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("essay");
  const [editing, setEditing] = useState<ExcerptusTemplate | null>(null);
  const [preview, setPreview] = useState<ExcerptusTemplate | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      setTemplates(await fetchAllExcerptusTemplates());
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) void refresh();
  }, [isAdmin]);

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error("Give the template a name");
      return;
    }
    setCreating(true);
    try {
      const created = await createExcerptusTemplate({
        name: newName.trim(),
        content_type: newType,
        is_published: false,
      });
      setNewName("");
      toast.success("Template created");
      setEditing(created);
      void refresh();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (t: ExcerptusTemplate) => {
    if (!confirm(`Delete template "${t.name}"?`)) return;
    try {
      await deleteExcerptusTemplate(t.id);
      toast.success("Deleted");
      void refresh();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <>
    <AppSeo title="Composer templates" />
    <div className="container mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/admin">
            <ArrowLeft className="mr-1 h-4 w-4" /> Back to Admin
          </Link>
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-semibold">Composer templates (Excerptus catalog)</h1>
        <p className="text-sm text-muted-foreground">
          Curate the global template library shown to all users in the Composer.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Create new template</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-[1fr_200px_auto] sm:items-end">
          <div className="grid gap-1.5">
            <Label htmlFor="ct-name">Name</Label>
            <Input
              id="ct-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g., Faith & Work essay"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ct-type">Content type</Label>
            <Select value={newType} onValueChange={setNewType}>
              <SelectTrigger id="ct-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTENT_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleCreate} disabled={creating}>
            {creating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
            Create
          </Button>
        </CardContent>
      </Card>

      <div>
        <h2 className="mb-3 text-lg font-semibold">All templates</h2>
        {loading ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        ) : templates.length === 0 ? (
          <p className="text-sm text-muted-foreground">No templates yet — create one above.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {templates.map((t) => (
              <TemplateCard
                key={t.id}
                name={t.name}
                description={t.description}
                contentType={t.content_type}
                badge={t.is_published ? "Published" : "Draft"}
                kind={templateKind(t)}
                onPreview={() => setPreview(t)}
                onEdit={() => setEditing(t)}
                onDelete={() => handleDelete(t)}
              />
            ))}
          </div>
        )}
      </div>

      {preview && (
        <TemplatePreviewDialog
          open
          onOpenChange={(o) => !o && setPreview(null)}
          name={preview.name}
          contentType={preview.content_type}
          description={preview.description}
          introMessage={preview.intro_message}
          wizardQuestions={preview.wizard_questions}
          defaultStructure={preview.default_structure}
          readOnlyReason="Preview only"
        />
      )}

      {editing && (
        <TemplateEditor
          mode="excerptus"
          template={editing}
          open={Boolean(editing)}
          onOpenChange={(o) => !o && setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      )}
    </div>
    </>
  );
};

export default AdminComposerTemplates;
